import { Config } from "@/config/Config";
import { TokenResponse, UserInfoResponse, ChangePasswordDTO } from "@/contracts/auth-api.types";
import { setAccessToken, clearAccessToken, apiRequest } from "./apiClient";

export const authService = {
  /**
   * Realiza login por senha (Password Grant) na Auth API.
   * O cookie refresh_token (HttpOnly) é manipulado automaticamente pelo navegador via credentials: 'include'.
   */
  async login(username: string, password: string): Promise<TokenResponse> {
    const clientId = Config.defaultClientId;
    const response = await fetch(`${Config.authApiUrl}/auth/token`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Client-Id": clientId,
        Accept: "application/json",
      },
      body: JSON.stringify({
        grant_type: "password",
        username,
        password,
        client_id: clientId,
      }),
    });

    if (!response.ok) {
      let errorMessage = "Credenciais inválidas ou usuário não autorizado";
      try {
        const errorBody = await response.json();
        if (errorBody && errorBody.message) {
          errorMessage = errorBody.message;
        }
      } catch {
        // Fallback para mensagem genérica
      }
      throw new Error(errorMessage);
    }

    const data: TokenResponse = await response.json();
    setAccessToken(data.access_token);
    return data;
  },

  /**
   * Obtém informações do usuário autenticado a partir do endpoint OIDC /auth/userinfo.
   */
  async getUserInfo(): Promise<UserInfoResponse> {
    return await apiRequest<UserInfoResponse>("/auth/userinfo");
  },

  /**
   * Encerra a sessão atual invalidando os refresh tokens e colocando o JTI na blacklist.
   */
  async logout(): Promise<void> {
    try {
      await apiRequest<{ message: string }>("/auth/logout", {
        method: "POST",
      });
    } finally {
      clearAccessToken();
    }
  },

  /**
   * Altera a senha do usuário autenticado.
   */
  async changePassword(data: ChangePasswordDTO): Promise<{ message: string }> {
    return await apiRequest<{ message: string }>("/users/change-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  },
};
